import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeSlash, Envelope, Lock, User, Buildings, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import api, { useAuthStore } from '../store/authStore';
import { useTheme } from '../context/ThemeContext';
import { LuminexLockup, LuminexIcon } from '../components/LuminexLogo';
import toast from 'react-hot-toast';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Strong password rule: 8+ chars, upper, lower, digit, special
const PW_RULE = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

// Only allow same-origin redirect targets to prevent open-redirect abuse.
const SAFE_REDIRECT = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
};

export default function AuthPage({ mode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = SAFE_REDIRECT(searchParams.get('redirect') || searchParams.get('next'));
  const { login, error, clearError } = useAuthStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { theme } = useTheme();

  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  // Forgot-password inline modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  // Account type: "org" (organization admin, default) or "individual"
  const [accountType, setAccountType] = useState('org');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
  });

  useEffect(() => { setIsLogin(mode === 'login'); setRegisteredEmail(null); }, [mode]);

  // If already signed in and we hit /login (e.g. via the email "Open billing portal" button),
  // jump straight to the requested redirect target instead of showing the form.
  useEffect(() => {
    if (mode === 'login' && isAuthenticated && redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [mode, isAuthenticated, redirectTo, navigate]);

  const handleChange = (e) => {
    clearError();
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back to Luminex!');
        navigate(redirectTo || '/library');
      } else {
        // Client-side guards (backend re-validates)
        if (!PW_RULE.test(formData.password)) {
          throw new Error('Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.');
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        const finalCompanyName = accountType === 'individual'
          ? `${(formData.firstName || '').trim()} ${(formData.lastName || '').trim()}`.trim() + "'s workspace"
          : formData.companyName;
        if (!finalCompanyName) throw new Error('Please enter your organization name');

        const { data } = await api.post('/api/auth/register', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          companyName: finalCompanyName,
          accountType,
        });
        setRegisteredEmail(data.email);
        toast.success('Verification email sent');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Authentication failed';
      toast.error(typeof msg === 'string' ? msg : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;
    try {
      await api.post('/api/auth/resend-verification', { email: registeredEmail });
      toast.success('Verification email re-sent');
    } catch { toast.error('Could not resend email'); }
  };

  const openForgotModal = () => {
    // Prefill with whatever the user has already typed in the login form.
    setForgotEmail((formData.email || '').trim());
    setForgotSent(false);
    setForgotError('');
    setForgotOpen(true);
  };

  const submitForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    const email = (forgotEmail || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setForgotError('Please enter a valid email address.');
      return;
    }
    setForgotSubmitting(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setForgotSent(true);
    } catch (err) {
      // Backend returns 429 on rate-limit — surface that specifically. All
      // other cases return 200 (anti-enumeration), so we still show success.
      if (err?.response?.status === 429) {
        setForgotError(err?.response?.data?.detail || 'Too many requests. Please try again in a few minutes.');
      } else {
        setForgotSent(true);
      }
    } finally {
      setForgotSubmitting(false);
    }
  };

  const toggleMode = () => {
    clearError();
    setRegisteredEmail(null);
    setIsLogin(!isLogin);
    navigate(isLogin ? '/register' : '/login');
  };

  return (
    <div data-testid="auth-page" style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1.1fr 1fr', background: 'var(--bg-0)' }}>
      {/* ═══════ LEFT — EDITORIAL PANEL (Obsidian + diamond motif) ═══════ */}
      <div className="auth-left-panel" style={{
        position: 'relative',
        background: 'var(--gradient-obsidian)',
        color: '#F3F4F6',
        padding: '56px 64px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '70%', height: '70%', background: 'radial-gradient(ellipse at top right, rgba(242,107,31,0.18), transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: -120, bottom: -120, opacity: 0.06, pointerEvents: 'none', color: '#5790C9' }}>
          <LuminexIcon size={560} />
        </div>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'a\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23a)\' opacity=\'0.4\'/%3E%3C/svg%3E")', opacity: 0.05, mixBlendMode: 'overlay', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 2 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, color: '#5790C9' }}>
            <LuminexIcon size={34} />
            <span>
              <span style={{ fontFamily: '"Playfair Display", Georgia, serif', fontStyle: 'italic', fontSize: 28, fontWeight: 400, color: '#F3F4F6', letterSpacing: '-0.02em' }}>
                Lumine<span style={{ color: '#F26B1F' }}>x</span>
              </span>
            </span>
          </span>
        </div>

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 520 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#F26B1F', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 18 }}>
            Process Intelligence for Capital Projects
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 46, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', color: '#F3F4F6', marginBottom: 22 }}>
            Turn legacy procedures into a <span style={{ color: '#F26B1F', fontStyle: 'italic' }}>steering-committee</span>-grade savings deliverable.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(243,244,246,0.75)', marginBottom: 32, maxWidth: 480 }}>
            Upload a process document — PDF, scan, DOCX, BPMN — and Luminex&apos;s Generative AI engine extracts the as-is swimlane, lets you re-engineer it, then generates cycle-time, FTE, and RACI deltas in a steering-committee-ready PDF.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { k: 'Logical Operating Models', v: '30 industry verticals · L0 → L4 hierarchy · 7,400+ business processes' },
              { k: 'Document → Canvas, in 15 seconds', v: 'OCR fallback for scanned PDFs · auto-extracted actors, steps, KPIs' },
              { k: 'Re-engineering Delta Reports', v: 'Steps eliminated · handoffs reduced · cycle-time & FTE savings, printable' },
            ].map((f) => (
              <div key={f.k} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ marginTop: 4, color: '#5790C9', flexShrink: 0 }}>
                  <LuminexIcon size={16} />
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#F3F4F6', marginBottom: 2 }}>{f.k}</div>
                  <div style={{ fontSize: 12, color: 'rgba(243,244,246,0.6)', lineHeight: 1.5 }}>{f.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 28, borderTop: '1px solid rgba(243,244,246,0.12)', paddingTop: 22 }}>
          {[['30', 'Industry verticals'], ['7,400+', 'Business processes'], ['L4', 'Swimlane depth'], ['AI', 'Generative engine']].map(([v, l]) => (
            <div key={l}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, color: '#F3F4F6', lineHeight: 1 }}>{v}</div>
              <div style={{ fontSize: 10, color: 'rgba(243,244,246,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ RIGHT — AUTH FORM ═══════ */}
      <div className="auth-right-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="auth-mobile-brand" style={{ display: 'none', justifyContent: 'center', marginBottom: 24 }}>
            <LuminexLockup iconSize={30} showDevanagari={true} />
          </div>

          {/* ── Post-registration "check your email" state ── */}
          {registeredEmail ? (
            <div data-testid="check-email-screen" style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, borderRadius: '50%', background: 'rgba(242,107,31,0.12)', color: '#F26B1F', marginBottom: 18 }}>
                <PaperPlaneTilt size={28} weight="duotone" />
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, color: 'var(--text-1)', marginBottom: 10, letterSpacing: '-0.01em' }}>Check your inbox</h2>
              <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 6 }}>We sent a verification link to</p>
              <p style={{ fontSize: 14, color: 'var(--accent)', fontWeight: 700, marginBottom: 16 }}>{registeredEmail}</p>
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 20 }}>
                Click the link in the email to verify your address and activate your Luminex workspace. The link is valid for 24 hours.
              </p>
              <button onClick={handleResendVerification} data-testid="resend-verification-btn"
                className="btn btn-secondary btn-block" style={{ marginBottom: 12 }}>
                Re-send verification email
              </button>
              <button onClick={() => { setRegisteredEmail(null); setIsLogin(true); navigate('/login'); }} className="btn btn-primary btn-block" data-testid="back-to-login-btn">
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em' }}>
              {isLogin ? 'Sign in to your workspace' : (accountType === 'individual' ? 'Sign up as an individual' : 'Create your organization on Luminex')}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {isLogin ? 'Already invited by your admin? Use the password you set when accepting the invite.' : (accountType === 'individual' ? 'Solo workspace — explore the LOMs and create processes on your own. You can upgrade to a team workspace anytime.' : "You'll be the first admin of your organization. You can invite teammates from the Team tab once inside.")}
            </p>
          </div>

          {!isLogin && (
            <div data-testid="account-type-toggle" style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
              <button type="button" onClick={() => setAccountType('org')} data-testid="acct-org"
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: '1.5px solid ' + (accountType === 'org' ? 'var(--accent)' : 'var(--border-1, #D9DDE3)'),
                  background: accountType === 'org' ? 'var(--accent)' : 'transparent',
                  color: accountType === 'org' ? '#fff' : 'var(--text-1)' }}>
                <Buildings size={12} weight="duotone" style={{ verticalAlign: -2, marginRight: 6 }} /> Organization
              </button>
              <button type="button" onClick={() => setAccountType('individual')} data-testid="acct-individual"
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: '1.5px solid ' + (accountType === 'individual' ? 'var(--accent)' : 'var(--border-1, #D9DDE3)'),
                  background: accountType === 'individual' ? 'var(--accent)' : 'transparent',
                  color: accountType === 'individual' ? '#fff' : 'var(--text-1)' }}>
                <User size={12} weight="duotone" style={{ verticalAlign: -2, marginRight: 6 }} /> Individual
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">First Name</label>
                <div style={{ position: 'relative' }}>
                  <User size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type="text" name="firstName" className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                    placeholder="Ada" value={formData.firstName} onChange={handleChange} required={!isLogin} data-testid="first-name-input" />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Last Name</label>
                <div style={{ position: 'relative' }}>
                  <User size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                  <input type="text" name="lastName" className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                    placeholder="Lovelace" value={formData.lastName} onChange={handleChange} required={!isLogin} data-testid="last-name-input" />
                </div>
              </div>
            </div>
          )}

          {!isLogin && accountType === 'org' && (
            <div className="form-group">
              <label className="form-label">Organization Name</label>
              <div style={{ position: 'relative' }}>
                <Buildings size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                <input type="text" name="companyName" className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                  placeholder="Analytical Engines Ltd" value={formData.companyName} onChange={handleChange} required data-testid="company-name-input" />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                Joining an existing organization? Ask your admin to invite you instead — you'll receive an email link.
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{isLogin ? 'Email' : 'Company Email'}</label>
            <div style={{ position: 'relative' }}>
              <Envelope size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
              <input type="email" name="email" className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                placeholder="you@company.com" value={formData.email} onChange={handleChange} required data-testid="email-input" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
              <input type={showPassword ? 'text' : 'password'} name="password" className="input"
                style={{ paddingLeft: 32, paddingRight: 36, fontSize: 13 }}
                placeholder={isLogin ? 'Your password' : 'Min 8 chars · upper · lower · digit · special'}
                value={formData.password} onChange={handleChange} required minLength={8} data-testid="password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }}
                data-testid="toggle-password-btn">
                {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {!isLogin && formData.password && (
              <div data-testid="password-strength" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {[
                  { k: '8+ chars', pass: formData.password.length >= 8 },
                  { k: 'A-Z', pass: /[A-Z]/.test(formData.password) },
                  { k: 'a-z', pass: /[a-z]/.test(formData.password) },
                  { k: '0-9', pass: /\d/.test(formData.password) },
                  { k: 'special', pass: /[^A-Za-z0-9]/.test(formData.password) },
                ].map(c => (
                  <span key={c.k} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 100, background: c.pass ? 'rgba(14,124,107,0.12)' : 'var(--bg-3)', color: c.pass ? '#0E7C6B' : 'var(--text-3)', fontWeight: 600 }}>
                    {c.pass ? '✓' : '·'} {c.k}
                  </span>
                ))}
              </div>
            )}
            {isLogin && (
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <button type="button" onClick={openForgotModal} data-testid="forgot-password-link"
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, fontSize: 12, padding: 0 }}>
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                <input type={showPassword ? 'text' : 'password'} name="confirmPassword" className="input"
                  style={{ paddingLeft: 32, fontSize: 13 }}
                  placeholder="Re-enter your password" value={formData.confirmPassword} onChange={handleChange} required data-testid="confirm-password-input" />
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div style={{ fontSize: 11, color: '#7A1F1F', marginTop: 4 }}>Passwords don&apos;t match</div>
              )}
            </div>
          )}

          {error && (
            <div className="form-error" style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--danger-dim)', borderRadius: 'var(--radius)' }} data-testid="auth-error">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary btn-block" disabled={loading} data-testid="auth-submit-btn">
            {loading ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-2)', marginTop: 16 }}>
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button type="button" onClick={toggleMode} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} data-testid="toggle-auth-mode-btn">
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </p>

          <p style={{ textAlign: 'center', fontSize: 10, color: 'var(--text-3)', marginTop: 16 }}>
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
          </>
          )}
        </div>
      </div>
      {/* ═══════ FORGOT PASSWORD MODAL ═══════ */}
      {forgotOpen && (
        <div data-testid="forgot-password-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(15,19,28,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
          onClick={() => !forgotSubmitting && setForgotOpen(false)}>
          <div style={{ width: '100%', maxWidth: 440, background: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 12, padding: '32px 32px 28px', boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}>
            {forgotSent ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'rgba(242,107,31,0.12)', color: '#F26B1F', alignItems: 'center', justifyContent: 'center' }}>
                    <PaperPlaneTilt size={28} weight="duotone" />
                  </div>
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-1)', textAlign: 'center', marginBottom: 8, letterSpacing: '-0.01em' }}>Check your inbox</h3>
                <p style={{ fontSize: 13, color: 'var(--text-2)', textAlign: 'center', marginBottom: 6 }}>If an account exists for</p>
                <p style={{ fontSize: 14, color: 'var(--accent)', fontWeight: 700, textAlign: 'center', marginBottom: 16 }}>{forgotEmail}</p>
                <p style={{ fontSize: 12, color: 'var(--text-2)', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
                  we&apos;ve sent a password-reset link. The link is valid for <strong>60 minutes</strong> and can only be used once.
                </p>
                <button className="btn btn-primary btn-block" onClick={() => setForgotOpen(false)} data-testid="forgot-close-btn">Got it</button>
              </>
            ) : (
              <>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6, letterSpacing: '-0.01em' }}>Reset your password</h3>
                <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
                  Enter the email address associated with your Luminex account and we&apos;ll send you a secure link to set a new password.
                </p>
                <form onSubmit={submitForgotPassword}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div style={{ position: 'relative' }}>
                      <Envelope size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                      <input type="email" className="input" style={{ paddingLeft: 32, fontSize: 13 }}
                        placeholder="you@company.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required autoFocus data-testid="forgot-email-input" />
                    </div>
                  </div>
                  {forgotError && (
                    <div data-testid="forgot-error" style={{ padding: '8px 12px', background: 'var(--danger-dim, rgba(122,31,31,0.08))', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#7A1F1F' }}>
                      {forgotError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setForgotOpen(false)} disabled={forgotSubmitting} data-testid="forgot-cancel-btn">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={forgotSubmitting} data-testid="forgot-submit-btn">
                      {forgotSubmitting ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Send reset link'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
